import React from "react";

import { addCheckboxToColumns } from "./utils";

// components
import InputCheckbox from "../../Inputs/Checkbox";
import Spinner from "../../Spinner";
import Typography from "../../Text";
import TableLiteScrollable, { ScrollableTableProps } from "../TableLiteScrollable/TableLiteScrollable";
// css
import css from "./TableLiteBulk.module.scss";

/*
	This component handles bulk selection for TableLiteScrollable. In the future, if needed we could add support for TableLite
	It should be used alongside the useBulkSelection hook for updating the bulk selection state.
*/

declare interface TableLiteBulkProps extends ScrollableTableProps {
	onCheckboxChange: (row, isSelected) => void | Promise<void>;
	isRowChecked: (row) => boolean;
	selectAllValue?: boolean;
	onSelectAllPageChange?: (value) => void;
	onSelectAllClick?: (value) => void;
	selectAllActionLabel?: string;
	numberOfSelectionsLabel?: string;
	shouldShowClearSelection: boolean;
	clearAllSelections: () => void;
	isBulkSelectionEnabled?: boolean;
	isBulkSuperSelectAllEnabled: boolean;
	tableActionButtons: React.ReactNode[];
	isAnimated?: boolean;
	areBulkSelectionBoxesEnabled?: boolean;
	emptyChildren?: React.ReactNode;
}

const TableLiteBulk = (props: TableLiteBulkProps) => {
	const {
		columns,
		isLoading,
		onCheckboxChange,
		isRowChecked,
		selectAllValue,
		onSelectAllPageChange,
		onSelectAllClick,
		selectAllActionLabel,
		numberOfSelectionsLabel,
		shouldShowClearSelection,
		clearAllSelections,
		isBulkSelectionEnabled = true,
		isBulkSuperSelectAllEnabled = false,
		tableActionButtons = [],
		isAnimated = false,
		areBulkSelectionBoxesEnabled = true,
	} = props;

	// Here we add the checkbox to the first column of the table
	const newColumns = addCheckboxToColumns(
		columns,
		onCheckboxChange,
		isRowChecked,
		!areBulkSelectionBoxesEnabled,
		!isBulkSelectionEnabled,
		isAnimated
	);

	return (
		<div className={css.bulkTableContainer}>
			<div className={css.bulkTableControlsContainer}>
				<div className={` ${css.bulkTableControls} `}>
					<div className={css.checkAll}>
						<InputCheckbox
							value={selectAllValue}
							onChange={onSelectAllPageChange}
							disabled={!isBulkSelectionEnabled}
						/>
						<Typography weight="semi-bold" size="paragraph-3">
							Select All
						</Typography>
					</div>
					{isBulkSuperSelectAllEnabled && isBulkSelectionEnabled && !isLoading ? (
						<div className={css.selectAll}>
							{numberOfSelectionsLabel && (
								<Typography
									className={css.numberLabel}
									weight="semi-bold"
									size="paragraph-3"
									testID="number-of-selections-label"
								>
									{numberOfSelectionsLabel}
								</Typography>
							)}
							{selectAllValue === true && (
								<Typography weight="semi-bold" size="paragraph-3">
									•
								</Typography>
							)}
							{selectAllValue === true && !shouldShowClearSelection && (
								<div onClick={onSelectAllClick}>
									<Typography
										className={css.actionLabel}
										weight="semi-bold"
										size="paragraph-3"
										color="primary-5"
										testID="select-all-action-label"
									>
										{selectAllActionLabel}
									</Typography>
								</div>
							)}
							{shouldShowClearSelection && (
								<div onClick={clearAllSelections}>
									<Typography
										className={css.actionLabel}
										weight="semi-bold"
										size="paragraph-3"
										color="primary-5"
										testID="clear-selection-action-label"
									>
										Clear Selection
									</Typography>
								</div>
							)}
						</div>
					) : (
						<div>
							<Spinner />
						</div>
					)}

					<div className={css.actionButtons}>
						{tableActionButtons.map((button, index) => (
							<div key={`id-${index}`}>{button}</div>
						))}
					</div>
				</div>
			</div>

			<div
				className={`${css.tableContainer} ${isAnimated ? css.isAnimated : ""} ${isBulkSelectionEnabled ? "" : css.hideControls} `}
			>
				<TableLiteScrollable {...props} columns={newColumns} roundedTopCorners={!isBulkSelectionEnabled} />
			</div>
		</div>
	);
};

export default TableLiteBulk;
